import React from 'react'
import { flushSync } from 'react-dom'
import CloseIcon from '@ancon/wildcat-ui/shared/icons/close.svg'
import ChevronRightIcon from '@ancon/wildcat-ui/shared/icons/chevron-right.svg'
import { OrderFormat, ServiceTime } from '@ancon/wildcat-types'
import useTranslation from 'next-translate/useTranslation'

import HeadingText from '../../../app/components/HeadingText'
import Button from '../../../app/components/Button'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import {
  appSetIsCartDrawerVisible,
  appSetIsCartRailCollapsed,
} from '../../../app/store/appSlice'
import Spinner from '../../../app/components/Spinner'
import useDeviceSize from '../../../app/hooks/useDeviceSize'
import { CartDrawerId, CartRailShowCartButtonId } from '../../constants'

import CartHeaderSummary from './CartHeaderSummary'
import styles from './CartHeader.module.scss'

interface CartHeaderProps {
  title: string
  outletName: string
  orderFormat?: OrderFormat
  loading?: boolean
  serviceTime?: ServiceTime
  isPreOrder?: boolean
  additionalActions?: React.ReactNode
  /** The company the order is for */
  companyName?: string | null
  /** The time and place were set by the group order's host */
  isSetByHost?: boolean
  /** Changes the format and time; no Change button without it */
  onChangeSetup?: () => void
}

export default function CartHeader({
  title,
  outletName,
  orderFormat,
  serviceTime,
  loading = false,
  isPreOrder = false,
  additionalActions,
  companyName,
  isSetByHost = false,
  onChangeSetup,
}: CartHeaderProps) {
  const { t } = useTranslation('common')

  const dispatch = useAppDispatch()
  const { isLargeDesktop } = useDeviceSize()

  // The cart hides with the focused button, so focus goes back to the
  // cart rail's "Show cart", once the rail is there
  function closeCart(action: Parameters<typeof dispatch>[0]) {
    flushSync(() => {
      dispatch(action)
    })

    document.getElementById(CartRailShowCartButtonId)?.focus()
  }

  function handleCollapseClick() {
    closeCart(appSetIsCartRailCollapsed(true))
  }

  function handleDrawerCloseClick() {
    closeCart(appSetIsCartDrawerVisible(false))
  }

  return (
    <div className={styles.headerContainer}>
      <div className={styles.headerInnerContainer}>
        <div className={styles.headerTop}>
          <div className={styles.headerTopTitle}>
            <HeadingText as="h2">{title}</HeadingText>
            {loading && (
              <div className={styles.headerTopLoader}>
                <Spinner size="medium" />
              </div>
            )}
          </div>
          <div className={styles.headerTopActions}>
            {additionalActions}
            {/* The rail on large desktops collapses instead */}
            {isLargeDesktop ? (
              <Button
                variant="secondary"
                className={styles.headerCollapseButton}
                onClick={handleCollapseClick}
                aria-controls={CartDrawerId}
                aria-expanded
                aria-label={t('cart.collapse')}
                title={t('cart.collapse')}
              >
                <ChevronRightIcon aria-hidden />
              </Button>
            ) : (
              <Button
                className={styles.headerCloseButton}
                variant="secondary"
                onClick={handleDrawerCloseClick}
                aria-controls={CartDrawerId}
                aria-label={t('close')}
              >
                <CloseIcon aria-hidden />
              </Button>
            )}
          </div>
        </div>
        <CartHeaderSummary
          outletName={outletName}
          orderFormat={orderFormat}
          serviceTime={serviceTime}
          isPreOrder={isPreOrder}
          companyName={companyName}
          isSetByHost={isSetByHost}
          onChangeSetup={onChangeSetup}
        />
      </div>
    </div>
  )
}
